import React, { useState, useCallback, useEffect } from 'react'
import { jobApplicationService, WizardPreferences, TailoredResumeResponse } from '../services/jobApplicationService'
import { ResumeItem } from '../services/apiService'
import { ResumeFitAnalysisResponse } from '../services/jobApplicationService'
import { useMessageBox, MessageBox } from './MessageBox'
import './TailoredResumeWizard.css'

interface TailoredResumeWizardProps {
  isOpen: boolean
  onClose: () => void
  jobApplicationId: string
  fitAnalysis: ResumeFitAnalysisResponse | null
  availableResumes: ResumeItem[]
  onResumeGenerated: (resume: TailoredResumeResponse) => void
}

interface WizardState {
  baseResumeId: string | null
  qualityTier: 'standard' | 'premium'
  focusAreas: string[]
  tones: ('professional' | 'technical' | 'creative' | 'executive')[]
  length: 'concise' | 'standard' | 'comprehensive'
  keyAchievements?: string
  careerObjective?: string
  additionalNotes?: string
  currentStep: number
  showImpactExplanation: boolean
  validationErrors: Record<string, string>
}

const TOTAL_STEPS = 8

export const TailoredResumeWizard: React.FC<TailoredResumeWizardProps> = ({
  isOpen,
  onClose,
  jobApplicationId,
  fitAnalysis,
  availableResumes,
  onResumeGenerated,
}) => {
  const { messageBox, showMessageBox, hideMessageBox } = useMessageBox()
  const [isGenerating, setIsGenerating] = useState(false)
  const [wizardState, setWizardState] = useState<WizardState>({
    baseResumeId: null,
    qualityTier: 'standard',
    focusAreas: [],
    tones: [],
    length: 'standard',
    currentStep: 1,
    showImpactExplanation: true,
    validationErrors: {},
  })

  // Calculate best fit resume from fitAnalysis array
  // Filter out tailored resumes (they can't be used as base resumes)
  const bestFitAnalysis = fitAnalysis?.resumeFitAnalyses && fitAnalysis.resumeFitAnalyses.length > 0
    ? [...fitAnalysis.resumeFitAnalyses]
        .filter(analysis => !analysis.resumeId?.startsWith('tailored-')) // Exclude tailored resumes
        .sort((a, b) => b.fitScore - a.fitScore)[0]
    : null
  const bestFitResumeId = bestFitAnalysis?.resumeId || null
  const bestFitScore = bestFitAnalysis?.fitScore || null

  // Initialize with best fit resume if available (only regular resumes, not tailored)
  useEffect(() => {
    if (isOpen && bestFitResumeId && !bestFitResumeId.startsWith('tailored-') && !wizardState.baseResumeId) {
      setWizardState(prev => ({
        ...prev,
        baseResumeId: bestFitResumeId,
      }))
    }
  }, [isOpen, bestFitResumeId, wizardState.baseResumeId])

  const validateStep = useCallback((step: number): boolean => {
    const errors: Record<string, string> = {}
    
    if (step === 1 && !wizardState.baseResumeId) {
      errors.baseResumeId = 'Please select a base resume'
    }
    
    if (step === 2 && wizardState.focusAreas.length === 0) {
      errors.focusAreas = 'Please select at least one focus area'
    }
    
    if (step === 3 && wizardState.tones.length === 0) {
      errors.tones = 'Please select at least one tone/style'
    }
    
    if (step === 4 && !wizardState.length) {
      errors.length = 'Please select a length preference'
    }
    
    setWizardState(prev => ({ ...prev, validationErrors: errors }))
    return Object.keys(errors).length === 0
  }, [wizardState])

  const handleNext = useCallback(() => {
    if (!validateStep(wizardState.currentStep)) {
      return
    }
    
    if (wizardState.currentStep < TOTAL_STEPS) {
      setWizardState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1,
        validationErrors: {},
      }))
    }
  }, [wizardState, validateStep])

  const handleBack = useCallback(() => {
    if (wizardState.currentStep > 1) {
      setWizardState(prev => ({
        ...prev,
        currentStep: prev.currentStep - 1,
        validationErrors: {},
      }))
    }
  }, [wizardState.currentStep])

  const isReviewStep = wizardState.currentStep === TOTAL_STEPS

  const handleGenerate = useCallback(async () => {
    if (!wizardState.baseResumeId) {
      showMessageBox({
        type: 'error',
        title: 'Validation Error',
        message: 'Please select a base resume',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      return
    }

    // Validate that baseResumeId is not a tailored resume ID
    if (wizardState.baseResumeId.startsWith('tailored-')) {
      showMessageBox({
        type: 'error',
        title: 'Validation Error',
        message: 'Cannot use a tailored resume as a base resume. Please select a regular resume.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      return
    }

    setIsGenerating(true)
    try {
      const preferences: WizardPreferences = {
        focusAreas: wizardState.focusAreas,
        tones: wizardState.tones,
        length: wizardState.length,
        keyAchievements: wizardState.keyAchievements,
        careerObjective: wizardState.careerObjective,
        additionalNotes: wizardState.additionalNotes,
      }

      const tailoredResume = await jobApplicationService.generateTailoredResume(
        jobApplicationId,
        wizardState.baseResumeId,
        preferences,
        {
          qualityTier: wizardState.qualityTier,
        }
      )

      let resolvedResume = tailoredResume
      if (tailoredResume?.status === 'wizard_in_progress') {
        resolvedResume = await jobApplicationService.pollTailoredResume(jobApplicationId)
      }

      if (resolvedResume) {
        onResumeGenerated(resolvedResume)
      }
      onClose()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Generation Failed',
        message: error instanceof Error ? error.message : 'Failed to generate tailored resume. Please try again.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    } finally {
      setIsGenerating(false)
    }
  }, [wizardState, jobApplicationId, onResumeGenerated, onClose, showMessageBox])

  const generateImpactExplanation = useCallback((step: number): string => {
    switch (step) {
      case 1: {
        const selectedResume = availableResumes.find(r => r.id === wizardState.baseResumeId)
        const selectedAnalysis = fitAnalysis?.resumeFitAnalyses?.find(a => a.resumeId === wizardState.baseResumeId)
        const fitScore = selectedAnalysis?.fitScore || null
        return `✓ Your tailored resume will include all skills and experience from: ${selectedResume?.originalFilename || 'Selected Resume'}
✓ We'll emphasize the parts that match this job: This Position
✓ We won't add any skills or experience that aren't in your base resume
${fitScore ? `✓ This resume has a ${Math.round(fitScore)}% match with the job requirements` : ''}`
      }
      case 2: {
        const areas = wizardState.focusAreas
        if (areas.length === 0) return 'Select focus areas to see how they will be emphasized'
        return `✓ Selected areas will receive: More prominence, detail, and space
${areas.includes('Skills') ? '✓ Skills section will be: Expanded with detailed technical competencies and proficiency levels\n' : ''}
${areas.includes('Experience') ? '✓ Experience section will include: More detailed bullet points (3-7 per position) with achievements and metrics\n' : ''}
${areas.includes('Education') ? '✓ Education section will be: Placed prominently near the top with detailed coursework and honors\n' : ''}
${areas.includes('Projects') ? '✓ Projects will be: Featured as a separate section with detailed descriptions\n' : ''}
${areas.includes('Certifications') ? '✓ Certifications will be: Prominently displayed with expiration dates and credential IDs\n' : ''}`
      }
      case 3: {
        const tones = wizardState.tones
        if (tones.length === 0) return 'Select tone/styles to see how they will affect your resume language'
        const toneNames = tones.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' + ')
        return `✓ Language will be: ${toneNames} - ${tones.length > 1 ? 'blended styles' : tones[0] === 'professional' ? 'formal and polished' : tones[0] === 'technical' ? 'technically detailed' : tones[0] === 'creative' ? 'expressive and innovative' : 'strategic and leadership-focused'}
✓ Achievements will emphasize: ${tones.includes('technical') ? 'Technical depth' : ''}${tones.includes('technical') && tones.includes('professional') ? ' and ' : ''}${tones.includes('professional') || tones.includes('executive') ? 'Measurable business results' : 'Innovation and impact'}
${tones.includes('professional') && tones.includes('technical') ? '✓ Example phrasing: "Engineered scalable solutions using [technologies] that reduced costs by 30% and improved system reliability"\n' : ''}`
      }
      case 4: {
        const length = wizardState.length
        const details: Record<string, string> = {
          concise: '2-3 bullet points per position, focus on most recent 3-5 years',
          standard: '3-5 bullet points per position, include last 5-7 years',
          comprehensive: '5-7 bullet points per position, include full career history',
        }
        return `✓ Resume length: Approximately ${length === 'concise' ? '1 page' : length === 'standard' ? '2 pages' : '2+ pages'}
✓ Each position will have: ${details[length]}
✓ Recent positions: ${length === 'comprehensive' ? 'Full detail (5-7 bullets)' : length === 'standard' ? 'More detail (3-5 bullets)' : 'Focused detail (2-3 bullets)'}
✓ Sections included: Skills, Experience, Education${length === 'comprehensive' ? ', Projects, Certifications' : length === 'standard' ? ', Projects (if relevant)' : ''}`
      }
      case 5:
        return wizardState.keyAchievements
          ? `✓ These achievements will be: Featured prominently in relevant experience sections
✓ We'll expand on: Your specific achievements with more detail and context
✓ Formatting: Will be formatted as detailed bullet points with metrics and impact`
          : 'Enter key achievements to see how they will be featured'
      case 6:
        return wizardState.careerObjective
          ? `✓ Your career objective will appear: At the top of your resume, right after your name and contact info
✓ It will help recruiters: Quickly understand your goals, experience level, and fit for this role
✓ Formatting: Will be formatted as a professional summary paragraph, 2-3 sentences`
          : 'Enter a career objective or we will generate one based on your experience'
      case 7:
        return wizardState.additionalNotes
          ? `✓ We'll incorporate: Your specific preferences into the resume structure and content
✓ Special attention to: Any specific areas you mentioned - they'll get more prominence
✓ We'll ensure: Your preferences are reflected in section emphasis, content detail, and placement`
          : 'Add additional notes to guide the resume generation'
      default:
        return ''
    }
  }, [wizardState, availableResumes, bestFitScore])

  if (!isOpen) return null

  const selectedResume = availableResumes.find(r => r.id === wizardState.baseResumeId)
  const bestFitResume = bestFitResumeId
    ? availableResumes.find(r => r.id === bestFitResumeId)
    : null

  if (!isOpen) return null;

  return (
    <div className="wizard-overlay">
      <div className="wizard-modal tailored-resume-wizard">
        <div className="wizard-header">
          <h2 className="wizard-title">Tailored Resume Wizard</h2>
          <button 
            className="wizard-close" 
            onClick={onClose}
            aria-label="Close wizard"
          >
            ✕
          </button>
        </div>

        <div className="wizard-progress">
          <div className="progress-indicator">
            Step {wizardState.currentStep} of {TOTAL_STEPS}
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(wizardState.currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        <div className="wizard-content">
          {/* Step 1: Base Resume Selection */}
          {wizardState.currentStep === 1 && (
            <div className="wizard-step">
              <h3 className="step-title">Select Your Base Resume</h3>
              <p className="step-guidance">
                Select the resume to use as the foundation. All content in your tailored resume will come from this base resume.
                {bestFitResume && ' The system has identified which resume best matches this job.'}
              </p>

              {bestFitResume && (
                <div className={`resume-card best-fit ${wizardState.baseResumeId === bestFitResume.id ? 'selected' : ''}`}>
                  <div className="resume-card-header">
                    <span className="best-fit-badge">Best Fit</span>
                    {bestFitScore !== null && (
                      <span className="fit-score">
                        {Math.round(bestFitScore)}% Match
                      </span>
                    )}
                  </div>
                  <div className="resume-card-body">
                    <h4>{bestFitResume.originalFilename || bestFitResume.filename || 'Resume'}</h4>
                    <p className="resume-card-description">
                      This resume has a {bestFitScore !== null ? Math.round(bestFitScore) : 'high'}% match with the job requirements
                    </p>
                  </div>
                  <button
                    className="select-resume-btn"
                    onClick={() => setWizardState(prev => ({ ...prev, baseResumeId: bestFitResume.id }))}
                  >
                    {wizardState.baseResumeId === bestFitResume.id ? '✓ Selected' : 'Select This Resume'}
                  </button>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Or Select a Different Resume</label>
                <select
                  className="form-select"
                  value={wizardState.baseResumeId || ''}
                  onChange={(e) => setWizardState(prev => ({ ...prev, baseResumeId: e.target.value }))}
                >
                  <option value="">-- Select a resume --</option>
                  {availableResumes
                    .filter(resume => !resume.id?.startsWith('tailored-')) // Exclude tailored resumes
                    .map(resume => (
                      <option key={resume.id} value={resume.id}>
                        {resume.originalFilename || resume.filename || `Resume ${resume.id}`}
                      </option>
                    ))}
                </select>
                {wizardState.validationErrors.baseResumeId && (
                  <span className="error-message">{wizardState.validationErrors.baseResumeId}</span>
                )}
              </div>

              {wizardState.showImpactExplanation && selectedResume && (
                <div className="impact-explanation">
                  <h4>How This Affects Your Resume</h4>
                  <pre className="impact-text">{generateImpactExplanation(1)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Focus Areas */}
          {wizardState.currentStep === 2 && (
            <div className="wizard-step">
              <h3 className="step-title">What Should We Emphasize?</h3>
              <p className="step-guidance">
                Select which aspects to emphasize. Multiple selections are allowed - each will receive more prominence and detail.
                <strong> Tip:</strong> Choose areas most relevant to this job.
              </p>

              <div className="focus-areas-grid">
                {['Skills', 'Experience', 'Education', 'Projects', 'Certifications'].map(area => (
                  <div
                    key={area}
                    className={`focus-area-card ${wizardState.focusAreas.includes(area) ? 'selected' : ''}`}
                    onClick={() => {
                      setWizardState(prev => ({
                        ...prev,
                        focusAreas: prev.focusAreas.includes(area)
                          ? prev.focusAreas.filter(a => a !== area)
                          : [...prev.focusAreas, area],
                      }))
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={wizardState.focusAreas.includes(area)}
                      onChange={() => {}}
                    />
                    <div className="focus-area-content">
                      <h4>{area}</h4>
                      <p className="focus-area-description">
                        {area === 'Skills' && 'Technical skills, programming languages, tools, and technologies'}
                        {area === 'Experience' && 'Work history and professional experience'}
                        {area === 'Education' && 'Educational background, degrees, and academic achievements'}
                        {area === 'Projects' && 'Personal or professional projects, portfolio work'}
                        {area === 'Certifications' && 'Professional certifications, licenses, and credentials'}
                      </p>
                      <p className="focus-area-impact">
                        {area === 'Skills' && 'Will be prominently featured with detailed subcategories'}
                        {area === 'Experience' && 'Will be expanded with detailed bullet points and achievements'}
                        {area === 'Education' && 'Will be highlighted near the top with detailed coursework'}
                        {area === 'Projects' && 'Will be featured as a separate section with detailed descriptions'}
                        {area === 'Certifications' && 'Will be prominently displayed with expiration dates'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {wizardState.validationErrors.focusAreas && (
                <span className="error-message">{wizardState.validationErrors.focusAreas}</span>
              )}

            </div>
          )}

          {/* Step 3: Tone & Style */}
          {wizardState.currentStep === 3 && (
            <div className="wizard-step">
              <h3 className="step-title">Choose Your Resume's Tone & Style</h3>
              <p className="step-guidance">
                Choose the tone and style that matches the job. Multiple styles can be combined - we'll blend them to create a unique resume. The tone affects language, phrasing, and how achievements are described.
              </p>

              <div className="tone-grid">
                {(['professional', 'technical', 'creative', 'executive'] as const).map(tone => (
                  <div
                    key={tone}
                    className={`tone-card ${wizardState.tones.includes(tone) ? 'selected' : ''}`}
                    onClick={() => {
                      setWizardState(prev => ({
                        ...prev,
                        tones: prev.tones.includes(tone)
                          ? prev.tones.filter(t => t !== tone)
                          : [...prev.tones, tone],
                      }))
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={wizardState.tones.includes(tone)}
                      onChange={() => {}}
                    />
                    <div className="tone-content">
                      <h4>{tone.charAt(0).toUpperCase() + tone.slice(1)}</h4>
                      <p className="tone-description">
                        {tone === 'professional' && 'Formal, polished language. Emphasizes achievements with metrics and results.'}
                        {tone === 'technical' && 'Focuses on technical depth, specific technologies, and problem-solving. Uses technical terminology.'}
                        {tone === 'creative' && 'More expressive language, highlights innovation and creative problem-solving.'}
                        {tone === 'executive' && 'Strategic, leadership-focused. Emphasizes vision, team leadership, and business impact.'}
                      </p>
                      <p className="tone-best-for">
                        <strong>Best for:</strong>{' '}
                        {tone === 'professional' && 'Corporate environments, traditional industries, business roles'}
                        {tone === 'technical' && 'Engineering, IT, software development, technical roles'}
                        {tone === 'creative' && 'Design, marketing, creative roles, innovation-focused companies'}
                        {tone === 'executive' && 'Senior roles, executive positions, leadership roles'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {wizardState.validationErrors.tones && (
                <span className="error-message">{wizardState.validationErrors.tones}</span>
              )}

            </div>
          )}

          {/* Step 4: Length Preference */}
          {wizardState.currentStep === 4 && (
            <div className="wizard-step">
              <h3 className="step-title">How Detailed Should Your Resume Be?</h3>
              <p className="step-guidance">
                Choose how comprehensive your resume should be. This affects detail level and overall length. Consider the job level and industry standards.
              </p>

              <div className="length-options">
                {(['concise', 'standard', 'comprehensive'] as const).map(length => (
                  <div
                    key={length}
                    className={`length-card ${wizardState.length === length ? 'selected' : ''}`}
                    onClick={() => setWizardState(prev => ({ ...prev, length }))}
                  >
                    <input
                      type="radio"
                      name="length"
                      checked={wizardState.length === length}
                      onChange={() => {}}
                    />
                    <div className="length-content">
                      <h4>
                        {length === 'concise' && 'Concise (1 page)'}
                        {length === 'standard' && 'Standard (2 pages)'}
                        {length === 'comprehensive' && 'Comprehensive (2+ pages)'}
                      </h4>
                      <p className="length-description">
                        {length === 'concise' && 'Brief, focused resume. Highlights only the most relevant experience.'}
                        {length === 'standard' && 'Balanced resume with moderate detail. Most common format.'}
                        {length === 'comprehensive' && 'Detailed resume with extensive information. Best for senior roles.'}
                      </p>
                      <p className="length-best-for">
                        <strong>Best for:</strong>{' '}
                        {length === 'concise' && 'Entry-level roles, quick applications, roles that prefer brevity'}
                        {length === 'standard' && 'Mid-level roles, most professional positions, standard applications'}
                        {length === 'comprehensive' && 'Senior roles, executive positions, when showcasing extensive experience'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {wizardState.showImpactExplanation && (
                <div className="impact-explanation">
                  <h4>How This Affects Your Resume</h4>
                  <pre className="impact-text">{generateImpactExplanation(4)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Key Achievements (Optional) */}
          {wizardState.currentStep === 5 && (
            <div className="wizard-step">
              <h3 className="step-title">Highlight Specific Achievements</h3>
              <p className="step-guidance">
                Enter specific achievements, projects, or accomplishments to emphasize. Optional - we'll automatically identify the best achievements from your base resume if left blank.
                <strong> Tip:</strong> Include quantifiable results (numbers, percentages, dollar amounts).
              </p>

              <div className="form-group form-group-wide">
                <textarea
                  className="form-textarea"
                  rows={6}
                  placeholder="Example achievements:&#10;- Led team that increased revenue by 30% in Q4&#10;- Built system that processed 1M requests/day&#10;- Reduced deployment time by 50% through automation"
                  value={wizardState.keyAchievements || ''}
                  onChange={(e) => setWizardState(prev => ({ ...prev, keyAchievements: e.target.value }))}
                  maxLength={500}
                />
                <div className="char-counter">
                  {(wizardState.keyAchievements || '').length} / 500 characters
                </div>
              </div>

              {wizardState.showImpactExplanation && wizardState.keyAchievements && (
                <div className="impact-explanation">
                  <h4>How This Affects Your Resume</h4>
                  <pre className="impact-text">{generateImpactExplanation(5)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Career Objective (Optional) */}
          {wizardState.currentStep === 6 && (
            <div className="wizard-step">
              <h3 className="step-title">Career Objective or Summary</h3>
              <p className="step-guidance">
                Add a career objective or professional summary for the top of your resume. Optional - we'll generate one based on your experience and the job requirements if left blank.
                <strong> Tip:</strong> Keep it concise (2-3 sentences) and tailored to this job.
              </p>

              <div className="form-group form-group-wide">
                <textarea
                  className="form-textarea"
                  rows={5}
                  placeholder="Example: Experienced software engineer with 5+ years in full-stack development, seeking a senior role where I can lead technical initiatives and mentor junior developers. Specialized in React, Node.js, and cloud architecture."
                  value={wizardState.careerObjective || ''}
                  onChange={(e) => setWizardState(prev => ({ ...prev, careerObjective: e.target.value }))}
                  maxLength={300}
                />
                <div className="char-counter">
                  {(wizardState.careerObjective || '').length} / 300 characters
                </div>
              </div>

              {wizardState.showImpactExplanation && wizardState.careerObjective && (
                <div className="impact-explanation">
                  <h4>How This Affects Your Resume</h4>
                  <pre className="impact-text">{generateImpactExplanation(6)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Step 7: Additional Notes (Optional) */}
          {wizardState.currentStep === 7 && (
            <div className="wizard-step">
              <h3 className="step-title">Any Other Preferences?</h3>
              <p className="step-guidance">
                Add any other specific requirements or preferences. Optional - only include if you have specific preferences beyond what we've already covered.
                <strong> Examples:</strong> "Emphasize cloud experience (AWS, Azure)", "Highlight leadership roles", "Focus on recent projects from the last 2 years".
              </p>

              <div className="form-group form-group-wide">
                <textarea
                  className="form-textarea"
                  rows={6}
                  placeholder="Enter any additional preferences or instructions..."
                  value={wizardState.additionalNotes || ''}
                  onChange={(e) => setWizardState(prev => ({ ...prev, additionalNotes: e.target.value }))}
                  maxLength={1000}
                />
                <div className="char-counter">
                  {(wizardState.additionalNotes || '').length} / 1000 characters
                </div>
              </div>

              {wizardState.showImpactExplanation && wizardState.additionalNotes && (
                <div className="impact-explanation">
                  <h4>How This Affects Your Resume</h4>
                  <pre className="impact-text">{generateImpactExplanation(7)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Step 8: Review & Generate */}
          {wizardState.currentStep === 8 && (
            <div className="wizard-step">
              <h3 className="step-title">Review Your Preferences</h3>
              <p className="step-guidance">
                Review your selections below. Click 'Generate' to create your tailored resume (30-60 seconds).
              </p>

              <div className="review-summary">
                <div className="review-item">
                  <strong>📄 Base Resume:</strong>{' '}
                  {selectedResume?.originalFilename || selectedResume?.filename || 'Not selected'}
                  {bestFitResume && wizardState.baseResumeId === bestFitResume.id && bestFitScore !== null && (
                    <span className="review-badge">
                      ({Math.round(bestFitScore)}% match)
                    </span>
                  )}
                </div>
                <div className="review-item">
                  <strong>🎯 Focus Areas:</strong>{' '}
                  {wizardState.focusAreas.length > 0 ? wizardState.focusAreas.join(', ') : 'None selected'}
                </div>
                <div className="review-item">
                  <strong>🎨 Tone & Style:</strong>{' '}
                  {wizardState.tones.length > 0 
                    ? wizardState.tones.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' + ')
                    : 'None selected'}
                </div>
                <div className="review-item">
                  <strong>📏 Length:</strong>{' '}
                  {wizardState.length === 'concise' && 'Concise (1 page)'}
                  {wizardState.length === 'standard' && 'Standard (2 pages)'}
                  {wizardState.length === 'comprehensive' && 'Comprehensive (2+ pages)'}
                </div>
                <div className="review-item">
                  <strong>⚡ Quality:</strong>
                  <div className="quality-tier-options">
                    <label className={`quality-tier-card ${wizardState.qualityTier === 'standard' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="qualityTier"
                        checked={wizardState.qualityTier === 'standard'}
                        onChange={() => setWizardState(prev => ({ ...prev, qualityTier: 'standard' }))}
                      />
                      <span>
                        <strong>Standard</strong> - default quality, 5 Kairos tokens
                      </span>
                    </label>
                    <label className={`quality-tier-card ${wizardState.qualityTier === 'premium' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="qualityTier"
                        checked={wizardState.qualityTier === 'premium'}
                        onChange={() => setWizardState(prev => ({ ...prev, qualityTier: 'premium' }))}
                      />
                      <span>
                        <strong>Premium</strong> - highest quality, 25 Kairos tokens
                      </span>
                    </label>
                  </div>
                  {wizardState.qualityTier === 'premium' && (
                    <div className="quality-tier-disclaimer">
                      Standard already uses strong models. Premium is available, but results may be similar and not noticeably better. Premium pricing reflects higher model costs.
                    </div>
                  )}
                </div>
                {wizardState.keyAchievements && (
                  <div className="review-item">
                    <strong>⭐ Key Achievements:</strong>
                    <pre className="review-text">{wizardState.keyAchievements}</pre>
                  </div>
                )}
                {wizardState.careerObjective && (
                  <div className="review-item">
                    <strong>📝 Career Objective:</strong>
                    <pre className="review-text">{wizardState.careerObjective}</pre>
                  </div>
                )}
                {wizardState.additionalNotes && (
                  <div className="review-item">
                    <strong>💡 Additional Notes:</strong>
                    <pre className="review-text">{wizardState.additionalNotes}</pre>
                  </div>
                )}
              </div>

              <div className="generate-section">
                <button
                  className="generate-btn"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate My Tailored Resume'}
                </button>
                {isGenerating && (
                  <p className="generating-message">
                    We are generating your tailored resume now. This can take 1-2 minutes, and this screen will update automatically when it is ready.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="wizard-navigation">
            <button
              className="wizard-btn wizard-btn-secondary"
              onClick={handleBack}
              disabled={wizardState.currentStep === 1}
            >
              Back
            </button>
            {!isReviewStep && (
              <button
                className="wizard-btn wizard-btn-primary"
                onClick={handleNext}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
      {messageBox && <MessageBox {...messageBox} onClose={hideMessageBox} />}
    </div>
  )
}
